import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Resolver, useForm, UseFormReturn } from "react-hook-form";
import { useMutation, UseMutationResult, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { MarkAsReadCrypto, ReadReason } from "../types";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTime } from "luxon";
import { isNaN, omit } from "lodash";
import { postCryptoMarkedAsRead, putCryptoMarkedAsRead } from "../../../Api";
import { ORDER_BOOKS, TICKERS } from "../../../Api/endpoints";

const endDate = DateTime.now()
  .plus({
    days: 1,
  })
  .set({ hour: 22, minute: 0 })
  .toISO();

const formSchema = yup.object({
  name: yup.string().required(),
  exchange: yup.string().required(),
  quoteCoin: yup.string().optional(),
  endDate: yup.string().required().default(endDate),
  address: yup.string().optional(),
  reason: yup
    .string()
    .oneOf(Object.values(ReadReason))
    .default(ReadReason.LOW_LIQUIDITY),
  volumeToRemoveMarkAsRead: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .optional(),
});

export type MarkAsReadCryptoFormValues = yup.InferType<typeof formSchema>;

const resolver: Resolver<MarkAsReadCryptoFormValues> = yupResolver(formSchema);

type OpenProps = Partial<
  MarkAsReadCrypto & {
    cryptoSymbol: string;
    cryptoName: string;
    address: string;
  }
>;

interface ContextValue {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: any; //TODO: type this
  open: (props: OpenProps) => void;
  form: UseFormReturn<MarkAsReadCryptoFormValues>;
  createMutation: UseMutationResult<
    any,
    unknown,
    { data: MarkAsReadCryptoFormValues },
    unknown
  >;
  updateMutation: any; //TODO: type this better
  isEditMode: boolean; //TODO: type this better
}

const MarkAsReadCryptoContext = createContext<ContextValue | undefined>(
  undefined
);

export const useMarkAsReadCrypto = (): ContextValue => {
  const context = useContext(MarkAsReadCryptoContext);

  if (context === undefined) {
    throw new Error(
      "useMarkAsReadCrypto must be used within a MarkAsReadCryptoContext"
    );
  }

  return context;
};

interface ProviderProps {
  children: React.ReactNode;
}

export const MarkAsReadCryptoProvider = ({ children }: ProviderProps) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [markAsReadId, setMarkAsReadId] = useState<
    MarkAsReadCrypto["id"] | null
  >(null);

  const isEditMode = useMemo(() => !!markAsReadId, [markAsReadId]);

  const form = useForm<MarkAsReadCryptoFormValues>({
    defaultValues: formSchema.cast({}) as MarkAsReadCryptoFormValues,
    resolver,
    reValidateMode: "onSubmit",
  });

  const onClose = useCallback(() => {
    setIsOpen(false);
    form.reset();
  }, [setIsOpen, form]);

  const open = useCallback(
    (props: OpenProps) => {
      setMarkAsReadId(props?.id ?? null);
      setIsOpen(true);
      form.reset({
        ...(formSchema.cast({}) as MarkAsReadCryptoFormValues),
        ...omit(props, "cryptoSymbol"),
        name: props.cryptoSymbol,
      });
    },
    [form, setIsOpen, setMarkAsReadId]
  );

  const createMutation = useMutation(
    ({ data }: { data: MarkAsReadCryptoFormValues }) => {
      return postCryptoMarkedAsRead(data);
    }
  );

  const updateMutation = useMutation(
    ({
      id,
      data,
    }: {
      id: MarkAsReadCrypto["id"];
      data: MarkAsReadCryptoFormValues;
    }) => {
      return putCryptoMarkedAsRead(id, data);
    }
  );

  const onSubmit = useCallback(
    (formData: MarkAsReadCryptoFormValues) => {
      (isEditMode
        ? updateMutation.mutateAsync({
            id: markAsReadId!,
            data: formData,
          })
        : createMutation.mutateAsync({
            data: formData,
          })
      ).then(() => {
        onClose();
        queryClient.invalidateQueries([TICKERS, ORDER_BOOKS]);
        toast(
          `${isEditMode ? "Updated" : ""} ${formData.name} marked as read`,
          {
            theme: "dark",
            autoClose: 8000,
            pauseOnFocusLoss: false,
          }
        );
      });
    },
    [
      onClose,
      createMutation,
      updateMutation,
      queryClient,
      isEditMode,
      markAsReadId,
    ]
  );

  const value = {
    isOpen,
    onClose,
    open,
    onSubmit,
    form,
    createMutation,
    updateMutation,
    isEditMode,
  };

  return (
    <MarkAsReadCryptoContext.Provider value={value}>
      {children}
    </MarkAsReadCryptoContext.Provider>
  );
};
